import { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getLatLong, puntoGuardado } from '../../common/helpers';

const containerStyle = {
  width: '100%',
  height: '350px',
  borderRadius: '12px'
};

const PageLocation = ({ address, placeId, location }) => {
  // El pin del mapa va con style (lo pinta Google Maps): lleva el valor del tema, no su nombre
  const { palette } = useTheme();
  // Lo que contestó Google y para qué dirección (si cambia la dirección mientras contesta, esa respuesta ya no sirve)
  const [geocodificada, setGeocodificada] = useState({ direccion: null, coords: null });
  // Las coordenadas guardadas de la dirección; sin ellas (las anteriores a que se guardaran) se le pregunta a Google por la dirección.
  // Sin el script de Google Maps (sin internet o bloqueado) no hay mapa que mostrar y no se pregunta nada
  const guardadas = puntoGuardado(location);
  const hayMapas = !!window.google?.maps;
  const preguntarAGoogle = hayMapas && !!address && !guardadas;

  useEffect(() => {
    if (!preguntarAGoogle) return;
    let vigente = true;
    getLatLong(address).then((coords) => {
      if (vigente) setGeocodificada({ direccion: address, coords });
    });
    return () => { vigente = false; };
  }, [preguntarAGoogle, address]);

  const coords = hayMapas ? guardadas || (geocodificada.direccion === address ? geocodificada.coords : null) : null;
  const loading = preguntarAGoogle && geocodificada.direccion !== address;

  if (!address) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="primary.main">
          Ubicación
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Conoce donde está ubicado este negocio
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: 1, borderColor: 'sutil',
          height: '350px',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.100'
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : coords ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={coords}
            zoom={15}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              styles: [
                {
                  featureType: 'poi',
                  elementType: 'labels',
                  stylers: [{ visibility: 'off' }]
                }
              ]
            }}
          >
            <OverlayView
              position={coords}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  position: 'absolute',
                  transform: 'translate(-50%, -100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  zIndex: 1000
                }}
              >
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  padding: '4px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  width: '56px',
                  height: '56px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  border: `1px solid ${palette.grey[200]}`
                }}>
                  <LocationOnIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                  <div style={{
                    position: 'absolute',
                    bottom: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '0',
                    height: '0',
                    borderLeft: '10px solid transparent',
                    borderRight: '10px solid transparent',
                    borderTop: '12px solid white',
                  }} />
                </div>
              </div>
            </OverlayView>
          </GoogleMap>
        ) : (
          <Typography color="text.secondary">No se pudo cargar el mapa</Typography>
        )}
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {address}
      </Typography>
    </Box>
  );
};

export default PageLocation;
